import { yupResolver } from '@hookform/resolvers/yup'
import { DateOnly } from '@if/core/date'
import { t } from '@if/core/i18n'
import { formatCurrencyNoDecimals } from '@if/core/utils'
import * as yup from '@if/core/yup'
import {
  ControlledDateOnlyPicker,
  ControlledNumberField,
  ControlledPercentField,
  ErrorAlert,
  Help,
  StatCard,
  useDebouncedWatch,
  useFormDialog,
} from '@if/mui'
import { Box, Card, CardContent, CardHeader, Divider, Grid, Skeleton, Stack, Typography } from '@mui/material'
import ApprovalLikelihoodBar, { ApprovalLikelihood } from '@src/components/ApprovalLikelihoodBar'
import {
  PromoPlanContentDto,
  PromoPlanDto,
  useGetCustomBuydownTermRates,
  useUpsertCustomBuydown,
} from '@src/data/api/merchants-api/merchants-api'
import { CUSTOM_BUYDOWN_FAMILY } from '@src/data/types/Constants'
import { UseFormReturn, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

const ADMIN_FEE_RATE = 0.06
const MERCHANT_FEE_RATE_MIN = 0.005
const MERCHANT_FEE_RATE_MAX = 0.2
// The smallest amount a loan can be issued for; the max financing amount cannot go below it.
const MIN_LOAN_AMOUNT = 500

const formatPercent = (value: number): string => `${(value * 100).toFixed(2)}%`
const buildPlanCode = (merchantFeeRate: number): string => `FLASH${Math.round(merchantFeeRate * 10000)}`

const TYPICAL_LOAN_AMOUNT = 10000

const getApprovalLikelihood = (merchantFeeRate: number): ApprovalLikelihood => {
  // TODO CHANGE FOR THE REAL ONE (this is a mocked calculator)
  if (merchantFeeRate < 0.05) return 'low'
  if (merchantFeeRate < 0.08) return 'moderate'
  if (merchantFeeRate < 0.12) return 'good'
  return 'high'
}

const CustomBuydownFormSchema = yup.object().shape({
  merchantFeeRate: yup
    .number()
    .min(MERCHANT_FEE_RATE_MIN, () => t('merchant.customBuydown.errors.merchantFeeRateMin', { min: 0.5 }))
    .max(MERCHANT_FEE_RATE_MAX, () => t('merchant.customBuydown.errors.merchantFeeRateMax', { max: 20 }))
    .required(),
  // Null means no maximum (unlimited). MIN_LOAN_AMOUNT is the minimum loan amount.
  maxFinancingAmount: yup
    .number()
    .min(MIN_LOAN_AMOUNT, () => t('merchant.customBuydown.errors.maxFinancingAmountMin', { min: MIN_LOAN_AMOUNT }))
    .defined()
    .nullable(),
  activeFrom: yup.dateOnly().required(),
  expiresOn: yup.dateOnly().defined().nullable().min(yup.ref('activeFrom')),
})

export type CustomBuydownFormValues = yup.InferType<typeof CustomBuydownFormSchema>

const getDefaults = (plan: PromoPlanDto | null): CustomBuydownFormValues => {
  if (!plan) {
    return {
      merchantFeeRate: MERCHANT_FEE_RATE_MIN,
      maxFinancingAmount: null,
      activeFrom: DateOnly.today(),
      expiresOn: null,
    }
  }

  return {
    merchantFeeRate: plan.merchantFeeRate,
    // Plan-level on the form; the backend stamps it onto every computed term rate.
    maxFinancingAmount: plan.promoTermRates[0]?.maxFinancingAmount ?? null,
    activeFrom: new DateOnly(plan.activeFrom),
    expiresOn: plan.expiresOn ? new DateOnly(plan.expiresOn) : null,
  }
}

const toContentDto = (values: CustomBuydownFormValues): PromoPlanContentDto => ({
  family: CUSTOM_BUYDOWN_FAMILY,
  label: buildPlanCode(values.merchantFeeRate),
  promoCodePrefix: buildPlanCode(values.merchantFeeRate),
  merchantFeeRate: values.merchantFeeRate,
  borrowerFeeRate: ADMIN_FEE_RATE,
  maxFinancingAmount: values.maxFinancingAmount ?? null,
  activeFrom: values.activeFrom.toString(),
  expiresOn: values.expiresOn?.toString() ?? null,
  supportsChargeback: false,
})

/**
 * Live per-term customer rate grid, fetched from the same backend calculation the Save path
 * freezes onto the plan. The backend owns the term list; terms it filtered out (e.g., by the
 * max-amount cap) are simply absent from the response.
 */
const RatePreview = ({ merchantId, form }: { merchantId: string; form: UseFormReturn<CustomBuydownFormValues> }) => {
  const { t } = useTranslation()

  const debouncedFeeRate = useDebouncedWatch({ control: form.control, name: 'merchantFeeRate' })
  const debouncedMaxAmount = useDebouncedWatch({ control: form.control, name: 'maxFinancingAmount' })
  const { isValid } = form.formState
  const canPreview =
    isValid &&
    debouncedFeeRate >= MERCHANT_FEE_RATE_MIN &&
    debouncedFeeRate <= MERCHANT_FEE_RATE_MAX &&
    (debouncedMaxAmount === null || debouncedMaxAmount >= 0)
  const [preview, isLoading, error] = useGetCustomBuydownTermRates(
    merchantId,
    debouncedFeeRate,
    debouncedMaxAmount,
    canPreview,
  )

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {t('merchant.customBuydown.availableTerms')}
      </Typography>
      {canPreview && error && <ErrorAlert error={error} disabledGutterBottom />}
      {canPreview && !error && isLoading && <Skeleton variant="rounded" height={72} />}
      {!canPreview && (
        <Typography variant="body2" color="text.secondary">
          {t('merchant.customBuydown.invalidTooltip')}
        </Typography>
      )}
      {canPreview && !error && preview && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 1.5 }}>
          {preview.map((rate) => (
            <StatCard
              key={rate.termMonths}
              variant="outlined"
              label={`${rate.termMonths} ${t('common.months')}`}
              value={formatPercent(rate.promoRate)}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

const SummaryFact = ({ label, value, caption }: { label: string; value: string; caption?: string }) => (
  <Box>
    <Typography variant="overline" color="primary" lineHeight={1.5} component="div">
      {label}
    </Typography>
    <Typography variant="h6" fontWeight="bold" component="div">
      {value}
    </Typography>
    {caption && (
      <Typography variant="caption" color="text.secondary" component="div">
        {caption}
      </Typography>
    )}
  </Box>
)

/**
 * Live plan summary, kept sticky beside the form: the derived plan code, what the contribution
 * costs the merchant on a typical loan, and a soft approval-likelihood signal. Reacts to the
 * contribution as it is typed (pure client-side math, no debounce needed).
 */
const PlanSummary = ({ form }: { form: UseFormReturn<CustomBuydownFormValues> }) => {
  const { t } = useTranslation()

  const merchantFeeRate = useWatch({ control: form.control, name: 'merchantFeeRate' })
  const isInRange =
    typeof merchantFeeRate === 'number' &&
    merchantFeeRate >= MERCHANT_FEE_RATE_MIN &&
    merchantFeeRate <= MERCHANT_FEE_RATE_MAX
  const likelihood = isInRange ? getApprovalLikelihood(merchantFeeRate) : null

  return (
    <Card variant="outlined">
      <CardHeader
        title={t('merchant.customBuydown.planSummary')}
        slotProps={{ title: { variant: 'subtitle1', color: 'primary' } }}
      />
      <CardContent sx={{ pt: 0 }}>
        <Stack gap={2} divider={<Divider />}>
          <SummaryFact
            label={t('merchant.customBuydown.planCode')}
            value={isInRange ? buildPlanCode(merchantFeeRate) : '—'}
          />
          <SummaryFact
            label={t('merchant.customBuydown.estimatedCost')}
            value={isInRange ? formatCurrencyNoDecimals(merchantFeeRate * TYPICAL_LOAN_AMOUNT) : '—'}
            caption={t('merchant.customBuydown.estimatedCostCaption', {
              amount: formatCurrencyNoDecimals(TYPICAL_LOAN_AMOUNT),
            })}
          />
          {likelihood ? (
            <ApprovalLikelihoodBar
              label={t('merchant.customBuydown.customerApproval')}
              likelihood={likelihood}
              hint={likelihood !== 'high' ? t('merchant.customBuydown.customerApprovalHint') : undefined}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('merchant.customBuydown.invalidTooltip')}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}

/**
 * Edit/create dialog for a merchant's single Custom Buydown promotion. The merchant edits the
 * contribution, the max financing amount, and the activation window; the terms and per-term
 * customer rates are computed server-side, the admin fee is locked, and the plan code is derived
 * from the contribution. Saving upserts through the PromoPlan endpoints (POST to mint, PUT to
 * append a revision).
 */
export function useCustomBuydownDialog(merchantId: string) {
  const { t } = useTranslation()
  const [upsertCustomBuydown] = useUpsertCustomBuydown()

  return useFormDialog<CustomBuydownFormValues, PromoPlanDto | null, PromoPlanDto>({
    title: (ctx) => (ctx.props ? t('merchant.customBuydown.editTitle') : t('merchant.customBuydown.createTitle')),
    fullWidth: true,
    maxWidth: 'md',
    defaultValues: (plan) => getDefaults(plan),
    resolver: yupResolver(CustomBuydownFormSchema),
    mode: 'onChange',
    successMessage: (saved) => t('merchant.customBuydown.saved', { planCode: saved.promoCodePrefix || saved.label }),
    confirmTooltip: (ctx) => (ctx.form.formState.isValid ? undefined : t('merchant.customBuydown.invalidTooltip')),
    content: ({ form }) => (
      <Grid container columns={12} spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Stack gap={2}>
            <Grid container columns={12} spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <ControlledPercentField
                  name="merchantFeeRate"
                  control={form.control}
                  label={t('merchant.customBuydown.contribution')}
                  decimals={2}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <ControlledNumberField
                  name="maxFinancingAmount"
                  control={form.control}
                  label={t('merchant.customBuydown.maxFinancingAmount')}
                  fullWidth
                  isMoney
                  disableDecimals
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <ControlledDateOnlyPicker
                  name="activeFrom"
                  control={form.control}
                  label={t('merchant.promo.activeFrom')}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <ControlledDateOnlyPicker
                  name="expiresOn"
                  control={form.control}
                  label={t('merchant.promo.expiresOn')}
                  clearable
                  fullWidth
                />
              </Grid>
            </Grid>
            <Divider />
            <RatePreview merchantId={merchantId} form={form} />
            <Divider />
            <Box>
              <Stack direction="row" alignItems="center" gap={0.5}>
                <Typography>{t('merchant.customBuydown.adminFee')}</Typography>
                <Help title={t('merchant.customBuydown.adminFeeTooltip')} />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {t('merchant.customBuydown.adminFeeLocked')}
              </Typography>
            </Box>
          </Stack>
        </Grid>
        {/* Sticky within the dialog's scroll area, so the summary stays visible while the form scrolls. */}
        <Grid size={{ xs: 12, md: 4 }} sx={{ position: 'sticky', top: 0, alignSelf: 'flex-start' }}>
          <PlanSummary form={form} />
        </Grid>
      </Grid>
    ),
    onSubmit: async (values, { props }) =>
      upsertCustomBuydown({ merchantId, planKey: props?.planKey ?? null, content: toContentDto(values) }),
  })
}
